import WasmController from "react-lib/frameworks/WasmController";

// Interface
import { State as MainState } from "../../../../../HIS/MainHISInterface";

// Common
import { SetProperty } from "../../common/CommonInterface";
import moment from "moment";

// Utils
import { dateToStringWithoutTimeBE } from "react-lib/utils/dateUtils";

// Serializer
import DrugTransferInterfaceSerializer from "issara-sdk/types/DrugTransferInterfaceSerializer_apps_INF";

// API
import DrugTransferInterfaceList from "issara-sdk/apis/DrugTransferInterfaceList_apps_INF";
import DrugTransferInterfaceUpdateView from "issara-sdk/apis/DrugTransferInterfaceUpdateView_apps_INF";
import UserList from "issara-sdk/apis/UserList_users";

export type Props = {

}

export type State = Partial<{
  // seq
  DrugTransferInterfaceSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    drugTransferInterfaceList: DrugTransferInterfaceSerializer[];
    selectedItems: any[];
    userOptions: any[];
    searchDrugTransfer: FilterSearchType;
    paginatedItems: PaginatedItemsType;
  }> | null;
}>;

export type FilterSearchType = Partial<{
  createdDate: string | null;
  productCode: string | null;
  sourceCode: string | null;
  destinationCode: string | null;
  vendorCode: string | null;
}>;

type PaginatedItemsType = Partial<{
  items: DrugTransferInterfaceSerializer[];
  page: number;
  pageSize: number;
  total: number;
}>;

type PickedState = Partial<
  Pick<MainState, "buttonLoadCheck" | "errorMessage" | "successMessage">
>;

export type PickedProps = Partial<Omit<PickedState, "">>;

// Sequence
type SeqState = {
  sequence: "DrugTransferInterface";
  restart?: boolean;
  clear?: boolean;
};

export type SaveType = "ADD" | "EDIT";

export type TableHeaderType<T> = {
  Header: string;
  accessor: keyof T;
  width?: number;
};

export type AccessorType = Partial<Record<keyof DrugTransferInterfaceSerializer, string>>;

export type WidthsType = Partial<Record<keyof DrugTransferInterfaceSerializer, number>>;

// Handle Action
type ActionType =
  | { action: "SEARCH"; cardKey: string; page?: number; pageSize?: number }
  | { action: "CLEAR"; cardKey: string }
  | { action: "SAVE"; cardKey: string; type: SaveType; selectedItems: any[] };

type SeqAct = ActionType & SeqState;
type SeqType<K> = K extends { action: string } ? Extract<SeqAct, K> : SeqState;

export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => void;

export type SetProp = SetProperty<State & PickedState>;

type CustomExtract<T, U> = T extends object ? (U extends Partial<T> ? T : never) : never;

type Params<A extends ActionType["action"]> = CustomExtract<ActionType, { action: A }>;

export const StateInitial: State = {
  // seq
  DrugTransferInterfaceSequence: {
    sequenceIndex: null,
  },
};

export type Event = { message: "RunSequence"; params: {} };

export type Data = {
  division?: number;
};

export const DataInitial = {};

export const CARD_NAME = "CardDrugTransferInterface";

type Optional<T> = {
  [K in keyof T]: `${typeof CARD_NAME}_${T[K] & string}`;
};

export const ACTIONS = {
  SEARCH: "SEARCH",
  CLEAR: "CLEAR",
  SAVE: "SAVE",
} as const;

export const BTN_ACTS = Object.fromEntries(
  Object.keys(ACTIONS).map((key) => [key, `${CARD_NAME}_${key}`])
) as Optional<typeof ACTIONS>;

const FILTER_INIT: FilterSearchType = {
  productCode: "",
  sourceCode: "",
  destinationCode: "",
  vendorCode: ""
};

export const LIST_LIMIT = 20;

const PAGINATED_INIT: PaginatedItemsType = {
  items: [],
  page: 0,
  total: 0,
  pageSize: LIST_LIMIT,
};

type Handler<P = any, R = any> = (
  controller: WasmController<State & PickedState, Event, Data, Props>,
  params: P
) => R;

/**========================================================================
 **                               START
 *========================================================================**/

export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  const currentDate = dateToStringWithoutTimeBE(moment());

  controller.setState(
    {
      DrugTransferInterfaceSequence: {
        ...state.DrugTransferInterfaceSequence,
        sequenceIndex: "Action",
        searchDrugTransfer: {
          createdDate: currentDate,
        },
        paginatedItems: {
          pageSize: LIST_LIMIT,
          page: 0,
        },
      },
    },
    // () => Action(controller, { ...params, action: "SEARCH", cardKey: CARD_NAME })
  );
};

/**========================================================================
 **                              ACTIONS
 *========================================================================**/

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers = {
    [ACTIONS.SEARCH]: HandleSearchData,
    [ACTIONS.CLEAR]: HandleClearData,
    [ACTIONS.SAVE]: HandleSaveData,
  };

  const action = params.action;

  if (action && actionHandlers[action]) {
    return actionHandlers[action](controller, params as any);
  }
};

const HandleSearchData: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  const searchDrugTransfer = state.DrugTransferInterfaceSequence?.searchDrugTransfer;
  const paginatedItems = state.DrugTransferInterfaceSequence?.paginatedItems;
  const page = (params?.page ?? paginatedItems?.page) || 0;
  const pageSize = (params?.pageSize ?? paginatedItems?.pageSize) || LIST_LIMIT;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SEARCH]: "LOADING",
    },
  });

  const offset = page * pageSize;

  let data = {
    limit: pageSize,
    offset,
    create_date: searchDrugTransfer?.createdDate,
    product_code: searchDrugTransfer?.productCode,
    source_storage_code: searchDrugTransfer?.sourceCode,
    destination_storage_code: searchDrugTransfer?.destinationCode,
    vendor_code: searchDrugTransfer?.vendorCode,
  };

  const result = await GetDrugTransferInterfaceData(controller, data);

  if (result?.items) {
    const uniqueData = (result.items || [])
      .filter(
        (item: DrugTransferInterfaceSerializer, index: number, self: any) =>
          index === self.findIndex((t: any) => t.created_by === item.created_by)
      )
      .map((item: DrugTransferInterfaceSerializer) => item?.created_by);

    const userOptions = await Promise.all(
      uniqueData.map(async (id: number) => {
        const user = await GetUserList(controller, { id });

        return user;
      })
    );

    controller.setState({
      DrugTransferInterfaceSequence: {
        ...state.DrugTransferInterfaceSequence,
        drugTransferInterfaceList: result?.items,
        userOptions: userOptions,
        paginatedItems: {
          ...paginatedItems,
          page,
          pageSize,
          total: result?.total,
        },
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "SUCCESS",
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "ERROR",
      },
    });
  }
};

const HandleSaveData: Handler<Params<"SAVE">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [`${CARD_NAME}_${params.type}`]: "LOADING",
    },
  });

  const [saveResp, saveErr] = await SaveDrugTransferInterface(controller, {
    type: params.type,
    selectedItems: params.selectedItems,
  });

  if (saveResp) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${CARD_NAME}_${params.type}`]: "SUCCESS",
      },
      successMessage: {
        ...state.successMessage,
        [CARD_NAME]: "The system has successfully",
      },
    });

    HandleSearchData(controller, { action: "SEARCH", cardKey: CARD_NAME });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${CARD_NAME}_${params.type}`]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: saveErr,
      },
    });
  }
};

const HandleClearData: Handler<Params<"CLEAR">> = async (controller, params) => {
  const state = controller.getState();

  const currentDate = dateToStringWithoutTimeBE(moment());

  controller.setState({
    DrugTransferInterfaceSequence: {
      ...state.DrugTransferInterfaceSequence,
      searchDrugTransfer: {
        ...FILTER_INIT,
        createdDate: currentDate,
      },
      drugTransferInterfaceList: [],
      paginatedItems: {
        ...PAGINATED_INIT,
      },
    }
  });
};

/**========================================================================
 **                               APIs
 *========================================================================**/

const GetDrugTransferInterfaceData: Handler = async (controller, params) => {
  const state = controller.getState();

  /* params: 'create_date', 'product_code', 'source_storage_code', 'destination_storage_code', 'vendor_code' */
  const [resp, err] = await DrugTransferInterfaceList.list({
    apiToken: controller.apiToken,
    params,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp;
};

const GetUserList: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await UserList.list({
    apiToken: controller.apiToken,
    params: { pk: params.id },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp?.items?.[0];
};

const SaveDrugTransferInterface: Handler = async (controller, params) => {
  const state = controller.getState();

  const apiType =
    params.type === "ADD"
      ? DrugTransferInterfaceUpdateView.post
      : DrugTransferInterfaceUpdateView.put;

  const [resp, err] = await apiType({
    apiToken: controller.apiToken,
    data: {
      item_id_list: params?.selectedItems || [],
    },
  });

  if (err) return [null, err];

  return [resp, null];
};

/**========================================================================
 **                               Utils
 *========================================================================**/

export const accessorList: Partial<Record<keyof DrugTransferInterfaceSerializer, string>> = {
  id: "ID",
  valid: "Valid",
  error_message: "Error Message",
  created_by: "User ที่สร้าง",
  created_at: "วันเวลาที่สร้าง",
  sent: "Sennt",
  file_name: "File Name",
  version: "Version",
  document_date: "Document Date",
  posting_date: "Posting Date",
  posting_time: "Posting Time",
  material_slip: "Material Slip",
  movement_type: "Movement Type",
  product_code: "Product Code",
  expiration_date: "Expiration Date",
  quantity: "Qty",
  unit: "unit",
  source_plant: "Source Plant",
  source_storage_code: "Source Storage Code",
  destination_plant: "Destination Plant",
  destination_storage_code: "Destination Storage Code",
  good_recipient: "Good Recipient",
  document_header_text: "Document Header Text",
  program_code: "Program Code",
  vendor_code: "Vendor Code",
  cost_center: "Cost Center",
};

export const GenerateTableHeader = (
  columnnWidths?: WidthsType
): TableHeaderType<DrugTransferInterfaceSerializer>[] => {
  return (Object.entries(accessorList) as [keyof DrugTransferInterfaceSerializer, string][]).map(
    ([key, label]) => ({
      Header: label,
      accessor: key,
      ...(columnnWidths && columnnWidths[key] ? { width: columnnWidths[key] } : {}),
    })
  );
};